/**
 * Report Accordion — presentation only.
 * ------------------------------------------------------------------
 * A single collapsible primitive shared by the interactive report
 * chapters. It is built on the native <details>/<summary> element for
 * three reasons:
 *
 *   • keyboard accessibility and the disclosure ARIA semantics come for
 *     free (Enter/Space toggle, focusable summary),
 *   • the PDF engine already force-opens every <details> in the capture
 *     clone (see expandCollapsibles in use-report-pdf), so an exported
 *     report always renders fully expanded, and
 *   • URL anchors keep working because the element itself carries the id.
 *
 * This module renders wording and structure only. It never reads,
 * derives, or changes report data.
 */

import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";

export function ReportAccordion({
  id,
  open,
  onOpenChange,
  summary,
  children,
  className = "",
  summaryClassName = "",
  bodyClassName = "",
  tone = "default",
  ...rest
}: {
  id?: string;
  /** Controlled open state. Omit for uncontrolled (native) behaviour. */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  summary: ReactNode;
  children: ReactNode;
  className?: string;
  summaryClassName?: string;
  bodyClassName?: string;
  tone?: "default" | "muted";
} & Record<`data-${string}`, string | undefined>) {
  const toneClass =
    tone === "muted"
      ? "border-border/60 bg-muted/30"
      : "border-border bg-card shadow-card";

  const controlled = typeof open === "boolean";

  return (
    <details
      id={id}
      {...rest}
      className={`group cg-accordion scroll-mt-24 overflow-hidden rounded-2xl border ${toneClass} ${className}`}
      {...(controlled ? { open } : {})}
      onToggle={(e) => {
        const next = (e.currentTarget as HTMLDetailsElement).open;
        if (next !== open) onOpenChange?.(next);
      }}
    >
      <summary
        className={`flex cursor-pointer list-none items-start gap-4 p-5 outline-none transition focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 md:p-6 ${summaryClassName}`}
      >
        <div className="min-w-0 flex-1">{summary}</div>
        <ChevronDown
          aria-hidden="true"
          className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
        />
      </summary>
      <div className={`cg-accordion-body border-t border-border/70 ${bodyClassName}`}>
        {children}
      </div>
    </details>
  );
}

/** Compact metadata pill used inside accordion headers. */
export function AccordionMetric({
  label,
  muted = false,
}: {
  label: string;
  muted?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
        muted
          ? "border-border/60 bg-muted/50 text-muted-foreground"
          : "border-border bg-secondary/50 text-foreground/75"
      }`}
    >
      {label}
    </span>
  );
}
